import { Injectable } from '@angular/core';
import {
    DomSanitizer,
    SafeResourceUrl,
    SafeUrl
} from '@angular/platform-browser';
import { ActivatedRouteSnapshot, Data, ActivationEnd } from '@angular/router';
import * as constants from '../constants-types/constants';
import { JobMatchValue } from '../constants-types/Enums';
import { List } from '../constants-types/PiList.model';
import { UniqueIdentifier } from '../constants-types/UniqueIdentifier.interface';
import { HttpHeaderContentType } from '../shared/Enums';
import { ArrayUtilities } from './Array.utility';
import { EventListenerUtility } from './EventListener.utility';
import { GuidUtilities } from './Guid.utility';
import { MathUtilities } from './Math.utility';
import { PDFUtilities } from './PDF.utility';
import { QueryStringUtility } from './QueryString.utility';
import { StringUtilities } from './String.utility';
import { SVGUtilities } from './SVG.utility';
import { UserAgentDetectUtilities } from './UserAgentDetect.utility';

const CHROME_PDF_TOOLS_OFF = '#toolbar=0';

@Injectable({ providedIn: 'root' })
export class Util {
    constructor(
        public strings: StringUtilities,
        public svg: SVGUtilities,
        public id: GuidUtilities,
        public array: ArrayUtilities,
        public eventListener: EventListenerUtility,
        public pdf: PDFUtilities,
        public userAgentDetect: UserAgentDetectUtilities,
        private sanitizer: DomSanitizer,
        public queryString: QueryStringUtility,
        public math: MathUtilities
    ) {}

    static hasValue(value: any) {
        return value !== undefined && value !== null;
    }

    hasValue = Util.hasValue;

    newDate(jsonDate: string | Date) {
        return jsonDate ? new Date(jsonDate) : null;
    }

    lowest(vals: number[]) {
        return vals.reduce((a, b) => (a > b ? b : a), Number.MIN_SAFE_INTEGER);
    }

    matchScoreColorClass(matchScore: number): string {
        if (matchScore >= constants.jobMatchCutoff[JobMatchValue.Great]) {
            return 'color-matchGreat';
        }
        if (matchScore >= constants.jobMatchCutoff[JobMatchValue.Ok]) {
            return 'color-matchOk';
        }
        if (matchScore >= constants.jobMatchCutoff[JobMatchValue.Low]) {
            return 'color-matchLow';
        }
        return 'color-matchPending';
    }

    generateQueryStringUrl(data: Map<string, string>): string {
        const retArray: string[] = [];
        data.forEach((value, key) => {
            if (value) {
                retArray.push(key + '=' + value);
            }
        });
        return retArray.join('&');
    }

    arrayBufferToBase64(buffer: ArrayBuffer): string {
        let binary = '';
        const bytes = new Uint8Array(buffer);
        const len = bytes.byteLength;
        for (let i = 0; i < len; i++) {
            binary += String.fromCharCode(bytes[i]);
        }
        return btoa(binary);
    }

    blobToSafeResourceUrl(
        blob: Blob,
        hideChromePdfTools = false
    ): SafeResourceUrl {
        let objectUrl = encodeURI(window.URL.createObjectURL(blob));
        if (hideChromePdfTools) {
            objectUrl += CHROME_PDF_TOOLS_OFF;
        }
        return this.sanitizer.bypassSecurityTrustResourceUrl(objectUrl);
    }

    blobToSafeUrl(blob: Blob): SafeUrl {
        const objectUrl = encodeURI(window.URL.createObjectURL(blob));
        return this.sanitizer.bypassSecurityTrustUrl(objectUrl);
    }

    async blobToArrayBuffer(blob: Blob): Promise<ArrayBuffer> {
        return new Promise<ArrayBuffer>((resolve) => {
            const reader = new FileReader();
            reader.readAsArrayBuffer(blob);
            reader.onloadend = function () {
                const base64data = reader.result as ArrayBuffer;
                resolve(base64data);
            };
        });
    }

    arrayBufferToSafeResourceUrl(
        buffer: ArrayBuffer,
        contentType: HttpHeaderContentType,
        hideChromePdfTools = false
    ): SafeResourceUrl {
        const blob = new Blob([buffer], {
            type: contentType
        });
        return this.blobToSafeResourceUrl(blob, hideChromePdfTools);
    }

    // https://stackoverflow.com/questions/12168909/blob-from-dataurl
    static dataURItoBlob(dataURI: string): Blob {
        // convert base64 to raw binary data held in a string
        // doesn't handle URLEncoded DataURIs - see SO answer #6850276 for code that does this
        const byteString = atob(dataURI.split(',')[1]);

        // separate out the mime component
        const mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0];

        // write the bytes of the string to an ArrayBuffer
        const ab = new ArrayBuffer(byteString.length);

        // create a view into the buffer
        const ia = new Uint8Array(ab);

        // set the bytes of the buffer to the correct values
        for (let i = 0; i < byteString.length; i++) {
            ia[i] = byteString.charCodeAt(i);
        }

        // write the ArrayBuffer to a blob, and you're done
        const blob = new Blob([ab], { type: mimeString });
        return blob;
    }

    isActivationEndForFullPath(event: ActivationEnd) {
        // ActivationEnd fires for each segment of the route. Check whether
        // this ActivationEnd corresponds to entire route
        return event.snapshot.children.length === 0;
    }

    getFullDataFromSnapshot(snapshot: ActivatedRouteSnapshot): Data {
        if (!snapshot) {
            return {};
        } else {
            return Object.assign(
                this.getFullDataFromSnapshot(snapshot.parent),
                snapshot.data
            );
        }
    }

    copyStringToClipboard(str: string) {
        const textArea = document.createElement('textarea');
        textArea.value = str;
        textArea.style.position = 'absolute';
        textArea.style.left = '-9999px';
        textArea.setAttribute('readonly', '');
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
    }

    static CSVToArray(csv: string) {
        const rows = csv.split('\n');
        return rows.map((row) => row.split(','));
    }

    readFileAsText(file: File): Promise<string> {
        return new Promise((resolve) => {
            const fileReader = new FileReader();
            fileReader.onload = function (e: ProgressEvent) {
                resolve((e.target as any).result);
            };
            fileReader.readAsText(file);
        });
    }

    reportFileName(
        firstName: string,
        lastName: string,
        reportName = '',
        lastNamePersonTwo = ''
    ) {
        if (!lastNamePersonTwo && !reportName) {
            return `${lastName}, ${firstName}`;
        } else if (!lastNamePersonTwo && reportName) {
            return `${lastName}, ${firstName} - ${reportName}`;
        } else {
            return `${lastName} & ${lastNamePersonTwo} - ${reportName}`;
        }
    }

    saferParse(str: string) {
        // The fallback can handle JSON without quotes, but fails when you have HTML
        // strings in your props so should only be a fallback
        try {
            return JSON.parse(str);
        } catch (error) {
            console.log(error);
            return JSON.parse(
                str.replace(/(['"])?([a-z0-9A-Z_]+)(['"])?:/g, '"$2": ')
            );
        }
    }

    // Next functions are here to make TypeScript happy and
    // serve as type guards
    isNumber(x: any): x is number {
        return typeof x === 'number';
    }

    isString(x: any): x is string {
        return typeof x === 'string';
    }

    setDifference(
        original: List<UniqueIdentifier>,
        modified: List<UniqueIdentifier>
    ) {
        const whatToDoWithId: {
            [key: string]: 'unchanged' | 'add' | 'delete';
        } = {};
        let addList: string[] = [];
        let deleteList: string[] = [];
        if (original !== modified) {
            modified.forEach((item) => {
                whatToDoWithId[item.id] = 'add';
            });
            original.forEach((item) => {
                whatToDoWithId[item.id] =
                    whatToDoWithId[item.id] === 'add' ? 'unchanged' : 'delete';
            });
            addList = Object.keys(whatToDoWithId).filter(
                (id) => whatToDoWithId[id] === 'add'
            );
            deleteList = Object.keys(whatToDoWithId).filter(
                (id) => whatToDoWithId[id] === 'delete'
            );
        }
        return {
            addList,
            deleteList
        };
    }
}
